import { DESKTOP_APP_SETTING_GROUPS, type SettingGroupName } from '@ytclip/shared'
import { Save } from 'lucide-react'
import { useCallback, useState } from 'react'
import { Button } from '@/components/ui/button.tsx'
import { Card, CardBody } from '@/components/ui/card.tsx'
import { ErrorState, LoadingBlock } from '@/components/ui/feedback.tsx'
import { PageHeader } from '@/components/ui/misc.tsx'
import { Tabs, TabPanel } from '@/components/ui/tabs.tsx'
import { isDirty, useSettingsDraft } from '@/features/admin/use-settings-draft.ts'
import { MenuPanel } from './app-menu.tsx'
import { GroupFields } from './setting-control.tsx'

/** The menu is a table, not a setting, so it gets a tab id of its own. */
const MENU_TAB = 'menu'

function isDesktopGroup(group: SettingGroupName): boolean {
  return DESKTOP_APP_SETTING_GROUPS.includes(group)
}

/**
 * Everything the desktop app is told, in one place.
 *
 * Version, banner and sidebar are three different storage shapes — two setting
 * groups and a table — but they are one job: deciding what every installation
 * sees at launch. Splitting them across the general settings page and a
 * separate menu screen meant nobody could see that job whole.
 */
export function AdminDesktopAppPage() {
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const settings = useSettingsDraft(useCallback(isDesktopGroup, []))

  const tabs = [
    ...settings.groups.map((group) => ({
      id: group.group,
      label: group.label,
      badge: group.settings.filter((setting) => isDirty(setting, settings.draft)).length,
    })),
    { id: MENU_TAB, label: 'Menu' },
  ]

  const active = tabs.find((tab) => tab.id === activeTab)?.id ?? tabs[0]?.id ?? MENU_TAB
  const activeGroup = settings.groups.find((group) => group.group === active)

  return (
    <>
      <PageHeader
        title="Aplikasi Desktop"
        description="Versi, banner, dan menu yang dilihat YT Short Clipper saat dibuka."
        action={
          // Hidden on the menu tab, which saves each row as it is edited.
          active === MENU_TAB ? undefined : (
            <Button
              disabled={settings.changed.length === 0}
              loading={settings.saving}
              onClick={settings.save}
              icon={<Save className="size-4" />}
            >
              Simpan{settings.changed.length > 0 ? ` (${settings.changed.length})` : ''}
            </Button>
          )
        }
      />

      {settings.isPending ? (
        <LoadingBlock />
      ) : settings.isError ? (
        <ErrorState message="Pengaturan aplikasi tidak bisa dimuat." />
      ) : (
        <Card>
          <CardBody className="pt-2">
            <Tabs
              label="Bagian aplikasi desktop"
              items={tabs}
              activeId={active}
              onChange={setActiveTab}
            />

            <TabPanel id={active}>
              {activeGroup ? (
                <GroupFields
                  settings={activeGroup.settings}
                  draft={settings.draft}
                  onChange={settings.setValue}
                />
              ) : (
                <MenuPanel />
              )}
            </TabPanel>
          </CardBody>
        </Card>
      )}
    </>
  )
}
